/**
 * SahaHome — mobile-first, icon-heavy home for the "saha_calisani"
 * (field-staff) role.
 *
 * UX rules (per user brief):
 *   • Sadece ikon ağırlıklı, Türkçe minimal metin. Yabancı dil bilen
 *     çalışanlar da anlayabilecek kadar sade.
 *   • Telefon önce — büyük dokunma hedefleri, tek elde kullanılabilir.
 *   • 4 sekme: Dün / Bugün / Yarın / Öbür Gün — 3 gün kapsamı + dün
 *     (arrived-early guests still need to be settled).
 *   • Her rezervasyon kartında:
 *       – Grup adı + acente + kişi/oda özeti
 *       – Alınacak: total_price (SAR)
 *       – Alınan: girilirse yeşile döner, tarih/saat + kim aldığı sistemine düşer
 *       – Oda ≤ 4 ise "Oda Numaraları" alanı (opsiyonel — free text)
 *       – "Sorun Bildir" — modal + admin'e gider ("incelemede" status)
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import panelApi from "@/lib/panelApi";
import { useAcenteAuth } from "@/context/AcenteAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { numberFieldProps } from "@/lib/numberInput";
import {
  Hotel, Users, BedDouble, Wallet, Check, LogOut, AlertTriangle,
  Loader2, KeyRound, ClipboardEdit, RefreshCw, DoorOpen, Phone, Lock,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

function fmtSar(n) {
  return `${Number(n || 0).toLocaleString("tr-TR")} ﷼`;
}
function fmtDateShort(iso) {
  if (!iso) return "";
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", weekday: "short" });
}
function fmtStamp(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

const DAY_ICON_CLASS = {
  "Dün":      "bg-slate-100 text-slate-700",
  "Bugün":    "bg-brand-orange text-white",
  "Yarın":    "bg-emerald-500 text-white",
  "Öbür Gün": "bg-brand-navy text-white",
};

export default function SahaHome() {
  const { user, logout } = useAcenteAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabIdx, setTabIdx] = useState(1); // start on "Bugün"
  const [editing, setEditing] = useState(null); // { row, room_numbers, collected_amount }
  const [issue, setIssue] = useState(null);     // { row, note, busy }
  const [savingId, setSavingId] = useState(null);
  // Şifre değiştirme modalı — saha çalışanları kendi şifrelerini yenileyebilsin.
  const [pw, setPw] = useState(null); // { old, next, confirm, busy }

  const load = useCallback(() => {
    setLoading(true);
    panelApi.get("/field/my-reservations")
      .then((r) => setData(r.data || null))
      .catch((e) => toast.error(e?.response?.data?.detail || "Veri alınamadı"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const activeDay = data?.days?.[tabIdx];
  const rows = activeDay?.items || [];

  const openEdit = (r) => setEditing({
    row: r,
    room_numbers: r.room_numbers || "",
    collected_amount: r.collected_amount ?? "",
  });
  const closeEdit = () => setEditing(null);

  const saveEdit = async () => {
    if (!editing) return;
    const r = editing.row;
    const payload = {};
    if (editing.room_numbers !== (r.room_numbers || "")) payload.room_numbers = editing.room_numbers;
    // Only send if user actually typed a number (blank means "don't touch").
    if (editing.collected_amount !== "" && editing.collected_amount !== null) {
      const n = Number(editing.collected_amount);
      if (Number.isFinite(n) && n >= 0) payload.collected_amount = n;
      else return toast.error("Geçerli bir tutar girin");
    }
    if (Object.keys(payload).length === 0) {
      closeEdit();
      return;
    }
    setSavingId(r.id);
    try {
      const resp = await panelApi.patch(`/field/reservations/${r.id}`, payload);
      // Reflect the fresh row into local state
      setData((prev) => {
        if (!prev) return prev;
        const days = prev.days.map((d) => ({
          ...d,
          items: d.items.map((x) => (x.id === r.id ? { ...x, ...resp.data } : x)),
        }));
        return { ...prev, days };
      });
      const expected = Number(r.total_price || 0);
      const collected = Number(payload.collected_amount ?? r.collected_amount ?? 0);
      if (payload.collected_amount !== undefined && collected < expected && expected > 0) {
        const missing = expected - collected;
        toast.warning(`Eksik: ${fmtSar(missing)} — kaydedildi.`, { duration: 6000 });
      } else {
        toast.success("Kaydedildi");
      }
      closeEdit();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSavingId(null);
    }
  };

  const openIssue = (r) => setIssue({ row: r, note: "", busy: false });
  const closeIssue = () => setIssue(null);

  const submitIssue = async () => {
    if (!issue) return;
    const r = issue.row;
    setIssue((s) => ({ ...s, busy: true }));
    try {
      await panelApi.post(`/field/reservations/${r.id}/report-issue`, { note: issue.note });
      toast.success("Sorun yöneticiye iletildi");
      // Optimistically update status
      setData((prev) => {
        if (!prev) return prev;
        const days = prev.days.map((d) => ({
          ...d,
          items: d.items.map((x) => (x.id === r.id ? { ...x, status: "incelemede", field_issue_note: issue.note } : x)),
        }));
        return { ...prev, days };
      });
      closeIssue();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gönderilemedi");
      setIssue((s) => (s ? { ...s, busy: false } : s));
    }
  };

  const openPw = () => setPw({ old: "", next: "", confirm: "", busy: false });
  const closePw = () => setPw(null);
  const submitPw = async () => {
    if (!pw) return;
    if (!pw.old) return toast.error("Mevcut şifrenizi girin");
    if (pw.next.length < 6) return toast.error("Yeni şifre en az 6 karakter olmalı");
    if (pw.next !== pw.confirm) return toast.error("Yeni şifreler eşleşmiyor");
    setPw((s) => ({ ...s, busy: true }));
    try {
      await panelApi.post("/me/password", { old_password: pw.old, new_password: pw.next });
      toast.success("Şifreniz güncellendi");
      closePw();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Şifre değiştirilemedi");
      setPw((s) => (s ? { ...s, busy: false } : s));
    }
  };

  const hotelName = data?.hotel_name || user?.hotel_name || "";

  return (
    <div className="min-h-screen bg-canvas-cream" data-testid="saha-home">
      {/* Header — sticky, big touch-friendly logout */}
      <header className="sticky top-0 z-30 bg-brand-navy text-white shadow-lg">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center gap-3">
          <img src="/asis-logo.png" alt="ASIS" className="w-9 h-9 object-contain flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand-gold">Saha Paneli</div>
            <div className="font-serif text-lg leading-tight truncate flex items-center gap-1.5">
              <Hotel size={14} className="text-brand-gold flex-shrink-0" />
              <span className="truncate" data-testid="saha-hotel-name">{hotelName || "—"}</span>
            </div>
          </div>
          <button
            onClick={load}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition"
            aria-label="Yenile"
            data-testid="saha-refresh"
            title="Yenile"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
          <button
            onClick={openPw}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition"
            aria-label="Şifre değiştir"
            data-testid="saha-open-password"
            title="Şifre değiştir"
          >
            <Lock size={16} />
          </button>
          <button
            onClick={logout}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition"
            aria-label="Çıkış"
            data-testid="saha-logout"
            title="Çıkış"
          >
            <LogOut size={16} />
          </button>
        </div>
      </header>

      <div className="max-w-md mx-auto p-3 space-y-3">
        {/* Tab strip — 4 day tabs */}
        <div className="grid grid-cols-4 gap-1 bg-white rounded-2xl border border-brand-navy/10 p-1" role="tablist" data-testid="saha-tabs">
          {(data?.days || [{label: "Dün"}, {label: "Bugün"}, {label: "Yarın"}, {label: "Öbür Gün"}]).map((d, i) => {
            const active = i === tabIdx;
            const count = d?.items?.length ?? 0;
            return (
              <button
                key={d.label}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTabIdx(i)}
                data-testid={`saha-tab-${i}`}
                className={`flex flex-col items-center gap-0.5 rounded-xl py-2.5 text-xs font-semibold transition ${
                  active
                    ? "bg-brand-orange text-white shadow-md"
                    : "text-slate-600 hover:bg-canvas-stone/60"
                }`}
              >
                <span>{d.label}</span>
                <span className={`text-[10px] font-mono rounded-full px-1.5 leading-4 ${active ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Reservations list */}
        {loading && !data ? (
          <div className="bg-white rounded-2xl border border-brand-navy/10 p-10 text-center text-slate-500">
            <Loader2 size={22} className="mx-auto mb-2 animate-spin text-brand-orange" />
            Yükleniyor…
          </div>
        ) : rows.length === 0 ? (
          <div className="bg-white rounded-2xl border border-brand-navy/10 p-10 text-center text-slate-500" data-testid="saha-empty">
            <div className="text-3xl mb-2">🕌</div>
            <div className="text-sm">Bu gün için giriş yok.</div>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="saha-list">
            {rows.map((r) => (
              <SahaCard
                key={r.id}
                row={r}
                onEdit={() => openEdit(r)}
                onIssue={() => openIssue(r)}
                busy={savingId === r.id}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Edit dialog — oda no + alınan para */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && closeEdit()}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="saha-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-serif text-brand-navy">
              <ClipboardEdit size={16} /> Rezervasyonu İşle
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-600">
              {editing?.row?.group_name || editing?.row?.reservation_number}
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              {/* Alınacak — büyük vurgulu, ödenecek ücret rezervasyon
                  açıldığında hemen üstte gözükür. Fallback: total_price 0
                  ise anlaşmalı fiyatlardan hesapla ki "0 SAR" gözükmesin. */}
              {(() => {
                const t = Number(editing.row.total_price || 0);
                let expected = t;
                if (!(expected > 0)) {
                  const rooms = ["r1","r2","r3","r4","r5"].reduce((s,k) => s + (editing.row.rooms?.[k] || 0), 0);
                  const nights = Number(editing.row.nights || 0);
                  const adults = Number(editing.row.adults || 0) + Number(editing.row.children || 0);
                  const roomTotal = Number(editing.row.agreed_room_price || 0) * rooms * nights;
                  const mealTotal = Number(editing.row.agreed_meal_price || 0) * adults * nights;
                  expected = roomTotal + mealTotal;
                }
                return (
                  <div className="rounded-xl bg-brand-orange/10 border border-brand-orange/30 p-4 text-center" data-testid="saha-edit-expected">
                    <div className="text-[10px] uppercase tracking-widest text-brand-orange-dark font-semibold">Alınacak Ücret</div>
                    <div className="font-serif text-3xl text-brand-navy mt-1">
                      {expected > 0 ? fmtSar(expected) : <span className="text-slate-400 italic text-lg">Fiyat henüz girilmemiş</span>}
                    </div>
                    {expected > 0 && !(t > 0) && (
                      <div className="text-[10px] text-slate-500 mt-1 italic">Anlaşmalı birim fiyatlardan hesaplandı</div>
                    )}
                  </div>
                );
              })()}

              {/* Müşteri telefonu — varsa göster ve dokunmatik-kolay bir
                  tıkla-ara butonu yerleştir. */}
              {editing.row.customer_phone && (
                <a
                  href={`tel:${editing.row.customer_phone}`}
                  className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 hover:bg-emerald-100 transition"
                  data-testid="saha-edit-phone"
                >
                  <div className="w-10 h-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
                    <Phone size={16} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold">Müşteri Telefonu</div>
                    <div className="font-mono text-lg text-brand-navy leading-tight">{editing.row.customer_phone}</div>
                  </div>
                </a>
              )}

              {/* Oda numaraları — artık her zaman görünür (kullanıcı isteği).
                  Kaç oda olduğu bilgisini ipucu satırında verir. */}
              {(() => {
                const totalRooms = ["r1","r2","r3","r4","r5"].reduce((s,k) => s + (editing.row.rooms?.[k] || 0), 0);
                return (
                  <div>
                    <Label className="flex items-center gap-1 mb-1"><DoorOpen size={13} className="text-brand-orange" /> Oda Numaraları <span className="text-slate-400 text-[10px] font-normal">(opsiyonel)</span></Label>
                    <Input
                      value={editing.room_numbers}
                      onChange={(e) => setEditing((s) => ({ ...s, room_numbers: e.target.value }))}
                      placeholder="Örn: 101, 102, 105"
                      data-testid="saha-edit-rooms"
                      inputMode="text"
                    />
                    <div className="text-[10px] text-slate-500 mt-1">Toplam {totalRooms} oda — virgülle ayırarak yazın</div>
                  </div>
                );
              })()}

              {/* Alınan para */}
              <div>
                <Label className="flex items-center gap-1 mb-1"><Wallet size={13} className="text-emerald-600" /> Alınan Para (SAR)</Label>
                <div className="relative">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editing.collected_amount}
                    onChange={(e) => setEditing((s) => ({ ...s, collected_amount: e.target.value }))}
                    placeholder="0"
                    className="pr-14 text-lg font-mono"
                    data-testid="saha-edit-collected"
                    {...numberFieldProps}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">﷼</span>
                </div>
                {editing.collected_amount !== "" && Number(editing.collected_amount) < Number(editing.row.total_price || 0) && Number(editing.row.total_price || 0) > 0 && (
                  <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 text-xs inline-flex items-center gap-1.5" data-testid="saha-edit-missing">
                    <AlertTriangle size={12} /> Eksik: <b>{fmtSar(Number(editing.row.total_price || 0) - Number(editing.collected_amount || 0))}</b>
                  </div>
                )}
              </div>

              {editing.row.collected_at && (
                <div className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 inline-flex items-center gap-1.5">
                  <Check size={11} /> Önce alınmış: <b>{fmtSar(editing.row.collected_amount || 0)}</b>
                  <span className="text-slate-500">· {fmtStamp(editing.row.collected_at)}</span>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeEdit} data-testid="saha-edit-cancel">Vazgeç</Button>
            <Button
              onClick={saveEdit}
              disabled={savingId === editing?.row?.id}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="saha-edit-save"
            >
              {savingId === editing?.row?.id ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Check size={14} className="mr-1" />}
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issue dialog */}
      <Dialog open={!!issue} onOpenChange={(o) => !o && closeIssue()}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="saha-issue-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-serif text-amber-700">
              <AlertTriangle size={16} /> Sorun Bildir
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-600">
              Rezervasyonda bir hata varsa yöneticiye iletin. Rezervasyon "İncelemede" olarak işaretlenir.
            </DialogDescription>
          </DialogHeader>
          {issue && (
            <div>
              <Label>Sorun Açıklaması</Label>
              <Textarea
                value={issue.note}
                onChange={(e) => setIssue((s) => ({ ...s, note: e.target.value }))}
                placeholder="Örn: Oda sayısı yanlış, misafir gelmedi, ödeme uyuşmuyor..."
                rows={4}
                data-testid="saha-issue-note"
                autoFocus
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeIssue} data-testid="saha-issue-cancel">Vazgeç</Button>
            <Button
              onClick={submitIssue}
              disabled={issue?.busy}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="saha-issue-submit"
            >
              {issue?.busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <AlertTriangle size={14} className="mr-1" />}
              Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Şifre değiştirme dialogu */}
      <Dialog open={!!pw} onOpenChange={(o) => !o && closePw()}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="saha-pw-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-serif text-brand-navy">
              <Lock size={16} /> Şifre Değiştir
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-600">
              Mevcut şifrenizle giriş yapıp yenisini belirleyin. En az 6 karakter.
            </DialogDescription>
          </DialogHeader>
          {pw && (
            <div className="space-y-3">
              <div>
                <Label>Mevcut Şifre</Label>
                <Input
                  type="password"
                  value={pw.old}
                  onChange={(e) => setPw((s) => ({ ...s, old: e.target.value }))}
                  autoFocus
                  data-testid="saha-pw-old"
                />
              </div>
              <div>
                <Label>Yeni Şifre</Label>
                <Input
                  type="password"
                  value={pw.next}
                  onChange={(e) => setPw((s) => ({ ...s, next: e.target.value }))}
                  data-testid="saha-pw-new"
                />
              </div>
              <div>
                <Label>Yeni Şifre (Tekrar)</Label>
                <Input
                  type="password"
                  value={pw.confirm}
                  onChange={(e) => setPw((s) => ({ ...s, confirm: e.target.value }))}
                  data-testid="saha-pw-confirm"
                />
                {pw.confirm && pw.next !== pw.confirm && (
                  <div className="text-[11px] text-rose-600 mt-1">Şifreler eşleşmiyor</div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closePw} data-testid="saha-pw-cancel">Vazgeç</Button>
            <Button
              onClick={submitPw}
              disabled={pw?.busy}
              className="bg-brand-navy hover:bg-brand-navy/90 text-white"
              data-testid="saha-pw-save"
            >
              {pw?.busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Check size={14} className="mr-1" />}
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SahaCard({ row: r, onEdit, onIssue, busy }) {
  const totalRooms = ["r1","r2","r3","r4","r5"].reduce((s,k) => s + (r.rooms?.[k] || 0), 0);
  const totalHeads = (r.adults || 0) + (r.children || 0) + (r.staff || 0);
  // Priced fee: prefer total_price; fall back to agreed unit prices so cards
  // don't misleadingly display "0 ﷼" when ops hasn't finalised the row yet.
  const rawExpected = Number(r.total_price || 0);
  let expected = rawExpected;
  if (!(expected > 0)) {
    const nights = Number(r.nights || 0);
    const adultsAll = Number(r.adults || 0) + Number(r.children || 0);
    const rt = Number(r.agreed_room_price || 0) * totalRooms * nights;
    const mt = Number(r.agreed_meal_price || 0) * adultsAll * nights;
    expected = rt + mt;
  }
  const collected = Number(r.collected_amount || 0);
  const done = collected > 0 && expected > 0 && collected >= expected;
  const partial = collected > 0 && expected > 0 && collected < expected;
  const isIssue = r.status === "incelemede";

  const cardTint = done
    ? "bg-emerald-50 border-emerald-300"
    : isIssue
      ? "bg-amber-50 border-amber-300"
      : "bg-white border-brand-navy/10";

  return (
    <li className={`rounded-2xl border shadow-sm ${cardTint} overflow-hidden`} data-testid={`saha-card-${r.id}`}>
      <div className="p-4">
        {/* Top row: agency + group + status pill + hotel badge (multi-hotel) */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="font-serif text-lg text-brand-navy leading-tight truncate" data-testid={`saha-group-${r.id}`}>
              {r.group_name || r.reservation_number}
            </div>
            <div className="text-[11px] text-slate-500 truncate flex items-center gap-1 flex-wrap">
              <span>{r.agency_name || "—"}</span>
              {r.hotel_name && (
                <span className="rounded-full bg-brand-navy/10 text-brand-navy px-1.5 py-0.5 text-[10px] inline-flex items-center gap-0.5" data-testid={`saha-card-hotel-${r.id}`}>
                  <Hotel size={9} /> {r.hotel_name}
                </span>
              )}
              {r.customer_phone && <> · <a href={`tel:${r.customer_phone}`} className="text-brand-orange"><Phone size={10} className="inline mb-0.5" /> {r.customer_phone}</a></>}
            </div>
          </div>
          {done && (
            <span className="text-[10px] uppercase tracking-widest bg-emerald-600 text-white rounded-full px-2 py-0.5 flex-shrink-0 inline-flex items-center gap-1" data-testid={`saha-status-done-${r.id}`}>
              <Check size={10}/> Tamam
            </span>
          )}
          {isIssue && (
            <span className="text-[10px] uppercase tracking-widest bg-amber-500 text-white rounded-full px-2 py-0.5 flex-shrink-0 inline-flex items-center gap-1">
              <AlertTriangle size={10}/> Bildirildi
            </span>
          )}
        </div>

        {/* Big icon stats — 2×2 grid */}
        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
          <StatChip icon={<Users size={14}/>} label="Kişi" value={totalHeads} sub={`${r.adults||0}Y·${r.children||0}Ç·${r.staff||0}G`} />
          <StatChip icon={<BedDouble size={14}/>} label="Oda" value={totalRooms} sub={r.room_numbers ? r.room_numbers : ""} />
        </div>

        {/* Money row */}
        <div className="mt-3 rounded-xl bg-brand-navy/[0.04] border border-brand-navy/10 p-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-brand-orange/15 text-brand-orange-dark flex items-center justify-center flex-shrink-0">
            <Wallet size={16} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Alınacak</div>
            <div className="font-serif text-xl text-brand-navy leading-tight" data-testid={`saha-expected-${r.id}`}>
              {expected > 0 ? fmtSar(expected) : <span className="text-slate-400 italic text-sm">Fiyat girilmemiş</span>}
            </div>
            {collected > 0 && (
              <div className="mt-1 text-[11px]" data-testid={`saha-collected-${r.id}`}>
                Alınan: <b className={done ? "text-emerald-700" : "text-amber-700"}>{fmtSar(collected)}</b>
                {r.collected_at && <span className="text-slate-500"> · {fmtStamp(r.collected_at)}</span>}
                {partial && (
                  <span className="text-amber-700 font-semibold"> · Eksik {fmtSar(expected - collected)}</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Actions — big touch targets, full-width */}
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Button
            onClick={onEdit}
            disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-700 text-white h-11 rounded-xl"
            data-testid={`saha-edit-btn-${r.id}`}
          >
            <ClipboardEdit size={14} className="mr-1" /> İşle
          </Button>
          <Button
            onClick={onIssue}
            variant="outline"
            className="border-amber-300 text-amber-800 hover:bg-amber-50 h-11 rounded-xl"
            data-testid={`saha-issue-btn-${r.id}`}
          >
            <AlertTriangle size={14} className="mr-1" /> Sorun Bildir
          </Button>
        </div>
      </div>
    </li>
  );
}

function StatChip({ icon, label, value, sub }) {
  return (
    <div className="rounded-xl bg-canvas-cream/60 border border-brand-navy/5 px-3 py-2 flex items-center gap-2 min-w-0">
      <div className="w-9 h-9 rounded-lg bg-white text-brand-navy flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
        <div className="font-serif text-lg text-brand-navy leading-none mt-0.5">{value}</div>
        {sub && <div className="text-[10px] text-slate-500 mt-0.5 truncate" title={sub}>{sub}</div>}
      </div>
    </div>
  );
}
